'use client';

import { useState, useEffect, useRef } from 'react';

import type { DateTimePickerProps } from '@mui/x-date-pickers-pro';
import { DateTimePicker } from '@mui/x-date-pickers-pro';

import { useTimezoneUtilsRef } from 'Utils/date/date';

type Props = {
  value: Date | null;
  onChange: (value: Date | null) => void;
  label?: React.ReactNode;
  className?: string | undefined;
  useLocalTimezone?: boolean;
} & DateTimePickerProps;

export const Or2DateTimePicker = ({
  value,
  onChange,
  useLocalTimezone = false,
  slotProps = {},
  label,
  className,
  ...other
}: Props) => {
  const timezoneUtilsRef = useTimezoneUtilsRef();
  // SelectedDatetime is always in local time - what we pass out via value and
  // on change may not be
  const [selectedDatetime, setSelectedDatetime] = useState<null | Date>(
    useLocalTimezone ? value : timezoneUtilsRef.current.asLocalTime(value),
  );

  // Use a ref here rather than state because we don't want to render when this changes
  // We just want to reference it when other things (namely value) changes
  const uncommittedRef = useRef(false);

  // Keep the selectedDatetime
  useEffect(() => {
    if (!uncommittedRef.current) {
      setSelectedDatetime(
        useLocalTimezone ? value : timezoneUtilsRef.current.asLocalTime(value),
      );
    }
  }, [value, timezoneUtilsRef, useLocalTimezone]);

  const toNormalizedDatetime = (date: Date | null) => {
    if (!date) {
      return date;
    }

    if (!useLocalTimezone) {
      // Put us into the correct timezone
      return timezoneUtilsRef.current.asUniversalTime(date);
    }

    return date;
  };

  const commitDate = (date = selectedDatetime) => {
    const normalizedDate = toNormalizedDatetime(date);
    if (
      (normalizedDate && normalizedDate.getTime()) !==
      (value && value.getTime())
    ) {
      onChange(normalizedDate);
    }

    uncommittedRef.current = false;
  };

  const handleBlur = () => {
    commitDate();
  };

  const handleChange = (date: Date | null) => {
    setSelectedDatetime(date);
    commitDate(date);
  };

  return (
    <DateTimePicker
      enableAccessibleFieldDOMStructure={false}
      className={className}
      label={label}
      format="dd/MM/yyyy HH:mm"
      value={selectedDatetime}
      onChange={handleChange}
      slotProps={{
        textField: { onBlur: handleBlur, ...slotProps.textField },
        ...slotProps,
      }}
      {...other}
    />
  );
};
